import { useState } from 'react'
import { ChevronDown, ChevronUp, History } from 'lucide-react'
import { usePreviousShifts } from '../hooks/usePreviousShifts'
import { useShiftDetail } from '../hooks/useShiftDetail'
import type { ShiftResponse } from '../types/shift.types'

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatDuration(startIso: string, endIso: string): string {
  const mins = Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000))
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

function PreviousShiftRow({ shift }: { shift: ShiftResponse }): JSX.Element {
  const [open, setOpen] = useState(false)
  const { data: detail, isLoading } = useShiftDetail(open ? shift.id : null)

  return (
    <li className="py-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between text-left">
        <div>
          <p className="text-sm font-medium text-gray-900">{shift.supervisor_name ?? 'Unknown'}</p>
          <p className="text-xs text-gray-400">{formatTime(shift.started_at)} → {shift.ended_at ? formatTime(shift.ended_at) : '—'}</p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="mt-2 space-y-1 rounded-lg bg-gray-50 p-3 text-sm">
          <p className="text-gray-700"><span className="text-gray-400">Duration:</span> {shift.ended_at ? formatDuration(shift.started_at, shift.ended_at) : '—'}</p>
          <p className="text-gray-700"><span className="text-gray-400">Completed tasks:</span> {isLoading ? '…' : detail?.tasks_done_count ?? 0}</p>
          <p className="whitespace-pre-wrap text-gray-700"><span className="text-gray-400">Notes:</span> {shift.notes?.trim() ? shift.notes : 'No notes.'}</p>
        </div>
      )}
    </li>
  )
}

export function PreviousShifts(): JSX.Element | null {
  const { data: shifts, isLoading, isError } = usePreviousShifts()

  if (isLoading || isError || !shifts || shifts.length === 0) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <History className="h-5 w-5 text-gray-500" />
        <h3 className="text-base font-semibold text-gray-900">Previous Shifts</h3>
      </div>
      <ul className="divide-y divide-gray-100">
        {shifts.map((s) => <PreviousShiftRow key={s.id} shift={s} />)}
      </ul>
    </div>
  )
}
